// Shared 3D math utilities for IVM playground components
// Rotation matrices, perspective projection, depth-sorted glow rendering

export type Vec3 = [number, number, number];

export interface ProjectedPoint {
  x: number;
  y: number;
  z: number;
}

export interface LineSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  z: number;
}

const TAU = Math.PI * 2;
const SQRT3 = Math.sqrt(3);

// --- Rotation matrices ---

export function rotateX(p: Vec3, a: number): Vec3 {
  const c = Math.cos(a),
    s = Math.sin(a);
  return [p[0], p[1] * c - p[2] * s, p[1] * s + p[2] * c];
}

export function rotateY(p: Vec3, a: number): Vec3 {
  const c = Math.cos(a),
    s = Math.sin(a);
  return [p[0] * c + p[2] * s, p[1], -p[0] * s + p[2] * c];
}

export function rotateZ(p: Vec3, a: number): Vec3 {
  const c = Math.cos(a),
    s = Math.sin(a);
  return [p[0] * c - p[1] * s, p[0] * s + p[1] * c, p[2]];
}

export function rotateXYZ(p: Vec3, rx: number, ry: number, rz: number): Vec3 {
  return rotateZ(rotateY(rotateX(p, rx), ry), rz);
}

// --- Perspective projection ---

export function project(
  p: Vec3,
  fov: number,
  dist: number,
): ProjectedPoint {
  const z = p[2] + dist;
  const scale = fov / z;
  return { x: p[0] * scale, y: p[1] * scale, z };
}

// --- Depth-sorted glow line rendering ---

export function renderGlowLines(
  ctx: CanvasRenderingContext2D,
  lines: LineSegment[],
  glowIntensity: number,
): void {
  if (lines.length === 0) return;

  // Sort back to front
  lines.sort((a, b) => a.z - b.z);

  let maxZ = -Infinity,
    minZ = Infinity;
  for (const l of lines) {
    if (l.z > maxZ) maxZ = l.z;
    if (l.z < minZ) minZ = l.z;
  }
  const zRange = maxZ - minZ || 1;

  ctx.save();
  ctx.shadowColor = "rgba(54, 150, 233, 0.5)";
  ctx.shadowBlur = glowIntensity;

  // Trinity blue depth gradient: #336699 (far) → #3369B6 (mid) → #3696E9 (near)
  for (const line of lines) {
    const depthNorm = (line.z - minZ) / zRange;
    const alpha = 0.08 + depthNorm * 0.85;
    const r = Math.floor(51 + depthNorm * 3);
    const g = Math.floor(102 + depthNorm * 48);
    const b = Math.floor(153 + depthNorm * 80);

    ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
    ctx.lineWidth = 0.5 + depthNorm * 1.2;
    ctx.beginPath();
    ctx.moveTo(line.x1, line.y1);
    ctx.lineTo(line.x2, line.y2);
    ctx.stroke();
  }

  ctx.restore();
}

// --- Vertex dot rendering ---

export function renderVertexDots(
  ctx: CanvasRenderingContext2D,
  points: ProjectedPoint[],
  cx: number,
  cy: number,
  zMin: number,
  zRange: number,
): void {
  for (const pt of points) {
    const depthNorm = (pt.z - zMin) / (zRange || 1);
    const alpha = 0.2 + depthNorm * 0.8;
    const size = 1 + depthNorm * 2;
    ctx.fillStyle = `rgba(54, 150, 233, ${alpha})`;
    ctx.beginPath();
    ctx.arc(cx + pt.x, cy + pt.y, size, 0, TAU);
    ctx.fill();
  }
}

// --- IVM background grid ---

export function drawIvmBackgroundGrid(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
): void {
  const side = 40;
  const hh = (side * SQRT3) / 2;
  const halfSide = side / 2;
  const rowCount = Math.ceil(h / hh) + 2;

  ctx.strokeStyle = "rgba(54, 105, 182, 0.03)";
  ctx.lineWidth = 1;
  ctx.beginPath();

  // Horizontal lines
  for (let row = 0; row <= rowCount; row++) {
    const y = row * hh;
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
  }

  // Left-leaning diagonals
  for (let x = -side; x <= w + side * 2; x += side) {
    for (let row = 0; row < rowCount; row++) {
      const x0 = row % 2 === 0 ? x : x + halfSide;
      ctx.moveTo(x0, row * hh);
      ctx.lineTo(x0 - halfSide, (row + 1) * hh);
    }
  }

  // Right-leaning diagonals
  for (let x = -side; x <= w + side * 2; x += side) {
    for (let row = 0; row < rowCount; row++) {
      const x0 = row % 2 === 0 ? x : x - halfSide;
      ctx.moveTo(x0, row * hh);
      ctx.lineTo(x0 + halfSide, (row + 1) * hh);
    }
  }

  ctx.stroke();
}

// --- Info overlay ---

export function drawInfoOverlay(
  ctx: CanvasRenderingContext2D,
  h: number,
  lines: string[],
): void {
  ctx.fillStyle = "rgba(54, 150, 233, 0.35)";
  ctx.font = "11px 'SF Mono', 'Fira Code', monospace";
  const lineHeight = 16;
  const startY = h - lines.length * lineHeight - 4;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], 16, startY + i * lineHeight);
  }
}
